import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect } from 'react';

const services = [
  {
    title: "NetSuite Licensing & Consulting",
    description: "Unlock the full potential of your business with expert NetSuite licensing and consulting services. We help you choose the right license and optimize your ERP setup for long-term growth.",
    icon: "📊",
    accentColor: "#2c3e50"
  },
  {
    title: "ERP Data Migration & Cloud Adoption",
    description: "Ensure a seamless transition with our ERP data migration and cloud adoption services. We help securely move your data and modernize your systems for improved scalability and performance.",
    icon: "☁️",
    accentColor: "#3498db"
  },
  {
    title: "Business Process Automation",
    description: "Boost efficiency with our business process automation solutions that streamline workflows and reduce manual tasks. Transform operations with smart, scalable automation tailored to your business needs.",
    icon: "⚡",
    accentColor: "#27ae60"
  },
  {
    title: "Customization & SuiteApp Development",
    description: "Enhance your NetSuite experience with tailored customization and SuiteApp development. We build scalable, business-specific solutions that extend functionality and align perfectly with your goals.",
    icon: "🛠️",
    accentColor: "#e74c3c"
  }
];

const ServiceCard = ({ service, index }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <Col md={6} className="mb-4">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={{
          hidden: { opacity: 0, y: 30 },
          visible: { 
            opacity: 1, 
            y: 0,
            transition: { delay: index * 0.15, duration: 0.6 }
          }
        }}
      >
        <Card className="h-100 border-0 shadow-sm bg-light">
          <Card.Body className="p-4">
            <div className="d-flex align-items-center mb-3">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: `${service.accentColor}20`,
                  color: service.accentColor
                }}
              >
                <span className="fs-4">{service.icon}</span>
              </div>
              <h5 
                className="mb-2"
                style={{ color: service.accentColor }}
              >
                {service.title}
              </h5>
            </div>
            <p className=" mb-2 text-black">{service.description}</p>
          </Card.Body>
        </Card>
      </motion.div>
    </Col>
  );
};

const HowWeHelp = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <section className="py-5 bg-white">
      <Container>
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0 },
            visible: { 
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
        >
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.h2
                className="display-5 fw-bold mb-3"
                variants={{
                  hidden: { opacity: 0, y: -20 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: { duration: 0.6 }
                  }
                }}
              >
                How We Help Your Business
              </motion.h2>
              
              <motion.p
                className="lead text-muted"
                variants={{
                  hidden: { opacity: 0 },
                  visible: { 
                    opacity: 1,
                    transition: { delay: 0.3, duration: 0.6 }
                  }
                }}
              >
                We provide end-to-end NetSuite solutions tailored for US businesses, from implementation and customization to automation and support. Streamline operations, enhance efficiency, and scale with confidence.
              </motion.p>
            </Col>
          </Row>
          
          <Row className='mb-3'>
            {services.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default HowWeHelp;